import {compact} from '@benchling/js-utils';
import {createStyles, Group, SimpleGrid, Text} from '@mantine/core';
import React, {useCallback} from 'react';

import {CoreObjectArchiveData} from 'app-core/platform/archivable/CoreObjectArchiveConstants';
import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import BenchlingConfig from 'BenchlingConfig';
import FolderSelector from 'file-browser/FolderSelector';
import {CustomTag} from 'folder-items/FileConstants';
import AliasField from 'metadata/AliasField';
import EditableStudiesList from 'studies/studyable/EditableStudiesList';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {DESKTOP_WIDTH, TABLET_WIDTH} from 'styles/ResponsiveConstants';
import Avatar, {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import usePreference from 'ui-library/hooks/usePreference';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

import DataCatalogTagTable from './DataCatalogTagTable';
import ShowMetadataToggle from './ShowMetadataToggle';

const MAX_TABLE_CELL_WIDTH_PX = 350;
const TABLE_GAP_BETWEEN_COLUMNS_PX = 42;
interface DataCatalogItem {
  id: string;
  name: string;
  creator: HasAvatar;
  createdAt: string;
  customFields: Array<CustomTag>;
  modifiedAt: string;
  folder: {id: string};
  permissions: {writable: boolean; archivable: boolean | null; unarchivable: boolean | null};
  archiveData: CoreObjectArchiveData;
}

export interface DataCatalogItemActionIcon {
  icon: BenchlingIconName;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}

const PREFERENCE_NAME = 'DATA_CATALOG_ITEM_DETAILS_EXPANDED';
type ExpandedPrefByKey = Record<string, boolean>;

/**
 * Track expand/collapse state, persisting in user preferences. Each preference key
 * (e.g. "file:text/plain") is tracked as a separate key in the preference object.
 */
function useMetadataExpanded(preferenceKey: string): [boolean, () => void] {
  const [expandedPrefByKey, setExpandedPrefByKey] = usePreference<ExpandedPrefByKey | undefined>(
    PREFERENCE_NAME
  );
  const isExpanded = expandedPrefByKey?.[preferenceKey] ?? true;
  const toggle = useCallback(() => {
    setExpandedPrefByKey({...expandedPrefByKey, [preferenceKey]: !isExpanded});
  }, [expandedPrefByKey, isExpanded, preferenceKey, setExpandedPrefByKey]);
  return [isExpanded, toggle];
}

const useStyles = createStyles((theme) => ({
  root: {
    '&&': {
      paddingTop: theme.spacing.lg,
      paddingBottom: theme.spacing.lg,
    },
  },
  titleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.xs,
  },
  titleContainer: {
    '&&': {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
    },
  },
  toggleWrapper: {},
  metadataTable: {
    backgroundColor: theme.colors.gray[0],
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.md,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
  },
  metadataCell: {
    alignItems: 'center',
    display: 'flex',
    fontSize: theme.fontSizes.md,
    gap: theme.spacing.lg,
    maxWidth: MAX_TABLE_CELL_WIDTH_PX,
    minWidth: 0,
    width: '100%',
  },
  metadataLabel: {
    color: theme.colors.gray[6],
    flex: 1,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  metadataValue: {
    // Grow to fill space twice as fast as labels since values are usually bigger.
    flex: 2,
    minWidth: 0,
  },
  metadataTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    marginBottom: theme.spacing.sm,
  },
  sectionGap: {
    marginTop: theme.spacing.md,
  },
}));

export interface MetadataField {
  label: string;
  value: React.ReactNode;
}

interface DataCatalogItemDetailsProps {
  dataCatalogItem: DataCatalogItem;
  extraColumns?: Array<MetadataField>;
  /** Title shown at the top of the metadata section, e.g. "File Metadata" or "Dataset Metadata". */
  metadataTitle?: string;
  tagsEmptyMessage: string;
  onChange: (updateParams: {
    id: string;
    name?: string;
    folderId?: string;
    customFields?: {[name: string]: string};
  }) => Promise<void>;
  actionIcons?: Array<DataCatalogItemActionIcon>;
  children?: React.ReactNode;
  /** Whether there is substantial content to show beyond the metadata (e.g. a file preview or data table). */
  hasContent: boolean;
  /** Key for persisting expand/collapse state per item type, e.g. "dataset" or "file:text/csv". */
  preferenceKey: string;
}

export default function DataCatalogItemDetails({
  dataCatalogItem,
  extraColumns,
  metadataTitle,
  onChange,
  tagsEmptyMessage,
  actionIcons,
  children,
  hasContent,
  preferenceKey,
}: DataCatalogItemDetailsProps): JSX.Element {
  const {classes} = useStyles();
  const isCollapsible = BenchlingConfig.PIPELINES_ENABLE_COLLAPSIBLE_DATA_CATALOG_ITEM_DETAILS && hasContent;
  const [isMetadataExpanded, toggleMetadataExpanded] = useMetadataExpanded(preferenceKey);

  const metadataFields = compact([
    // Row 1
    {
      label: 'Creator',
      value: (
        <div>
          <Avatar
            className="u-mini-spacing-right"
            size={AvatarSize.XXSMALL}
            entity={dataCatalogItem.creator}
          />
          {dataCatalogItem.creator.name}
        </div>
      ),
    },
    {
      label: 'Created',
      value: readableTime(dataCatalogItem.createdAt),
    },
    {
      label: 'Modified',
      value: readableTime(dataCatalogItem.modifiedAt),
    },
    // Row 2
    {
      label: 'Folder',
      value: (
        <FolderSelector
          value={dataCatalogItem.folder.id}
          onChange={(e: {target: {value: string}}) => {
            onChange({id: dataCatalogItem.id, folderId: e.target.value});
          }}
          showInventory={true}
          readOnly={!dataCatalogItem.permissions.writable}
          unsetOnFetchFail={false}
        />
      ),
    },
    BenchlingConfig.STUDIES_ENABLE
      ? {
          label: 'Studies',
          value: (
            <EditableStudiesList
              studyable={dataCatalogItem}
              isWritable={dataCatalogItem.permissions.writable}
            />
          ),
        }
      : null,
  ]);

  return (
    <div className={`react-tab-container ${classes.root}`}>
      <Group position="apart" align="flex-start" noWrap={true}>
        <div className={classes.titleContainer}>
          <div className={classes.titleRow}>
            <AliasField
              initialAlias={dataCatalogItem.name}
              isHeader={true}
              isWritable={dataCatalogItem.permissions.writable}
              complete={(name: string) => {
                onChange({id: dataCatalogItem.id, name});
              }}
              placeholder="Name"
            />
          </div>
          <ArchiveRecordLabel item={dataCatalogItem} />
        </div>
        <Group spacing="xs" noWrap={true}>
          {actionIcons?.map(({icon, title, onClick, disabled}) => (
            <Tooltip text={title} key={`${icon}-${title}`}>
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                icon={icon}
                disabled={disabled}
                onClick={onClick}
                size={ButtonSize.SMALL}
              />
            </Tooltip>
          ))}
          <ArchiveOrUnarchiveButton
            isArchivable={Boolean(dataCatalogItem.permissions.archivable)}
            isUnarchivable={Boolean(dataCatalogItem.permissions.unarchivable)}
            item={dataCatalogItem}
            onArchive={ArchiveActions.bulkArchiveFilesWithDataMods}
            onUnarchive={ArchiveActions.bulkUnarchiveFilesWithDataMods}
          />
          <ExportAuditLogButton ids={[dataCatalogItem.id]} />
        </Group>
      </Group>
      {isCollapsible && (
        <div className={classes.toggleWrapper}>
          <ShowMetadataToggle isExpanded={isMetadataExpanded} onToggle={toggleMetadataExpanded} />
        </div>
      )}
      {(isCollapsible ? isMetadataExpanded : true) && (
        <>
          <div className={classes.metadataTable}>
            {metadataTitle && <Text className={classes.metadataTitle}>{metadataTitle}</Text>}
            <SimpleGrid
              cols={3}
              spacing={TABLE_GAP_BETWEEN_COLUMNS_PX}
              verticalSpacing="xs"
              breakpoints={[
                {maxWidth: DESKTOP_WIDTH, cols: 2},
                {maxWidth: TABLET_WIDTH, cols: 1},
              ]}
            >
              {[...metadataFields, ...(extraColumns ?? [])].map((field, i) => (
                <div key={field?.label ?? `field-${i}`} className={classes.metadataCell}>
                  <Text className={classes.metadataLabel}>{field?.label}</Text>
                  <div className={classes.metadataValue}>{field?.value}</div>
                </div>
              ))}
            </SimpleGrid>
          </div>
          <div className={classes.sectionGap}>
            <DataCatalogTagTable
              emptyMessage={tagsEmptyMessage}
              isWritable={dataCatalogItem.permissions.writable}
              itemId={dataCatalogItem.id}
              initialTags={dataCatalogItem.customFields}
              updateTags={async (tags: Array<CustomTag>) => {
                const customFields: {[name: string]: string} = {};
                for (const tag of tags) {
                  customFields[tag.name] = tag.value!;
                }
                await onChange({id: dataCatalogItem.id, customFields});
              }}
            />
          </div>
        </>
      )}
      {children}
    </div>
  );
}
